import React, { useState, useEffect, useRef } from 'react';

/**
 * Props:
 *   onSubmit(code): Promise<void>
 *   onSwitchToTotp(): void
 *   submitting: boolean
 *   error: string
 */
export default function BackupCodeChallengeWidget({
  onSubmit,
  onSwitchToTotp,
  submitting,
  error,
  rememberDevice = false,
  onRememberDeviceChange,
  showRememberDevice = false,
  rememberDeviceDays = 30,
}) {
  const [code, setCode] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Server accepts with or without dash and any case
    if (code.replace(/[-\s]/g, '').length === 10) onSubmit(code);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-white mb-1">
          Backup code
        </label>
        <p className="text-xs text-gray-200 mb-2">
          Enter one of the backup codes you saved when you set up two-factor authentication.
          Each code works only once.
        </p>
        <input
          ref={inputRef}
          type="text"
          autoComplete="off"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          maxLength={13}
          className="block w-full px-3 py-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 font-mono text-lg tracking-widest text-center uppercase bg-white"
          placeholder="ABCDE-FGHJK"
          disabled={submitting}
        />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm" role="alert">
          {error}
        </div>
      )}

      {showRememberDevice && (
        <label className="flex items-center gap-2 text-sm text-gray-200 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={rememberDevice}
            onChange={(e) => onRememberDeviceChange && onRememberDeviceChange(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            data-testid="remember-device-checkbox"
          />
          Remember this device for {rememberDeviceDays} days
        </label>
      )}

      <button
        type="submit"
        disabled={submitting || code.replace(/[-\s]/g, '').length !== 10}
        className="w-full py-2 px-4 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
      >
        {submitting ? 'Verifying…' : 'Verify and sign in'}
      </button>

      <button
        type="button"
        onClick={onSwitchToTotp}
        className="block w-full text-center text-sm text-gray-200 hover:text-white underline"
      >
        Use authenticator app instead
      </button>
    </form>
  );
}
